import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SearchTypeSettings {
  oemEnabled: boolean;
  refEnabled: boolean;
}

export const useSearchTypeSettings = () => {
  const [settings, setSettings] = useState<SearchTypeSettings>({
    oemEnabled: true,
    refEnabled: true,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "search_type_settings")
        .maybeSingle();

      if (data && !error) {
        setSettings(data.setting_value as unknown as SearchTypeSettings);
      }
      setIsLoading(false);
    };

    fetchSettings();

    // Subscribe to realtime changes
    const channel = supabase
      .channel("search-type-settings-changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "site_settings",
          filter: "setting_key=eq.search_type_settings",
        },
        (payload) => {
          setSettings(payload.new.setting_value as unknown as SearchTypeSettings);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const updateSettings = async (newSettings: Partial<SearchTypeSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    
    const { error } = await supabase
      .from("site_settings")
      .upsert({
        setting_key: "search_type_settings",
        setting_value: updatedSettings,
      }, {
        onConflict: "setting_key"
      });

    if (!error) {
      setSettings(updatedSettings);
    }
    
    return { error };
  };

  return {
    settings,
    isLoading,
    updateSettings,
    oemEnabled: settings.oemEnabled,
    refEnabled: settings.refEnabled,
  };
};
